import { format } from 'date-fns';
import { allTimestampsOneYear } from '../../../../utils/allTimestampsOneYear';
import { localDateToDate } from '../../../../utils/localDateToDate';
import type {
  AnalyticsNumBedroomType,
  CustomerAnalyticsMarketRentPriceResponseDto,
  MoodysAnalyticsResponseDto,
  PortfolioAnalyticsPropertyResponseDto,
} from '@zg-rentals/ts-customer-analytics-client';

export type RentPriceChartData = {
  name: string;
  'Portfolio average': number | null;
  'Market average': number | null;
  'Portfolio average month to date': number | null;
  'Market average month to date': number | null;
  'Month to date': number | null;
  timestamp: number;
  count: number;
};

export type VacancyRatesChartData = {
  name: string;
  'Portfolio average': number | null;
  'Market average': number | null;
  'Portfolio average quarter to date': number | null;
  'Market average quarter to date': number | null;
  'Quarter to date': number | null;
  timestamp: number;
  count: number;
};

export type VacantUnitsChartData = {
  name: string;
  'Portfolio vacant units': number;
  'Portfolio occupied units': number;
  'Market vacant units': number;
  'Market occupied units': number;
  'QTD portfolio vacant units': number;
  'QTD portfolio occupied units': number;
  'QTD market vacant units': number;
  'QTD market occupied units': number;
  timestamp: number;
};

type GenerateRentPriceDataParams = {
  filteredProperties: Array<PortfolioAnalyticsPropertyResponseDto>;
  isSingleFamily: boolean;
  bedsFilter: AnalyticsNumBedroomType;
  marketAvgData?: Array<CustomerAnalyticsMarketRentPriceResponseDto>;
};

type GenerateVacancyRatesDataParams = {
  filteredProperties: Array<PortfolioAnalyticsPropertyResponseDto>;
  moodysData?: Array<MoodysAnalyticsResponseDto>;
};

type GenerateVacantUnitsDataParams = {
  filteredProperties: Array<PortfolioAnalyticsPropertyResponseDto>;
  moodysData?: Array<MoodysAnalyticsResponseDto>;
};

export const generateRentPriceData = ({
  filteredProperties,
  isSingleFamily,
  bedsFilter,
  marketAvgData,
}: GenerateRentPriceDataParams): Array<RentPriceChartData> => {
  const timestamps = allTimestampsOneYear();
  const propertiesForGraph = filteredProperties.filter((property) =>
    isSingleFamily ? property.aggregateStats.isSingleFamily : !property.aggregateStats.isSingleFamily,
  );

  const chartData: Array<RentPriceChartData> = [];

  timestamps.forEach((timestamp) => {
    const timestampDate = new Date(timestamp);
    const formattedDate = format(timestampDate, 'MMM yyyy');

    const marketAverage = marketAvgData?.find(
      (data) => data.date.year === timestampDate.getFullYear() && data.date.month === timestampDate.getMonth() + 1,
    );

    chartData.push({
      name: formattedDate,
      'Portfolio average': 0,
      'Market average': marketAverage?.avgPrice ?? 0,
      'Portfolio average month to date': null,
      'Market average month to date': null,
      'Month to date': null,
      timestamp,
      count: 0,
    });
  });

  propertiesForGraph.forEach((property) => {
    property.breakdownStats.forEach((stat) => {
      const statDate = localDateToDate(stat.statDate);
      const formattedDate = format(statDate, 'MMM yyyy');
      const currentData = chartData.find((data) => data.name === formattedDate);
      if (currentData && stat.averageRentPriceByBedroom && stat.averageRentPriceByBedroom[bedsFilter]) {
        const avgValue = Number(stat.averageRentPriceByBedroom[bedsFilter]);
        currentData['Portfolio average'] = (currentData['Portfolio average'] ?? 0) + (avgValue ?? 0);
        currentData.count++;
      }
    });
  });

  chartData.forEach(
    (data) => (data['Portfolio average'] = data.count > 0 ? (data['Portfolio average'] ?? 0) / data.count : 0),
  );

  const currentDateFormatted = format(new Date(), 'MMM yyyy');
  const mtdIndex = chartData.findIndex((data) => data.name === currentDateFormatted);

  // Set up the dashed MTD line connecting from previous month to current month
  if (mtdIndex > 0) {
    chartData[mtdIndex - 1]['Portfolio average month to date'] = chartData[mtdIndex - 1]['Portfolio average'];
    chartData[mtdIndex - 1]['Market average month to date'] = chartData[mtdIndex - 1]['Market average'];
    chartData[mtdIndex]['Portfolio average month to date'] = chartData[mtdIndex]['Portfolio average'];
    chartData[mtdIndex]['Market average month to date'] = chartData[mtdIndex]['Market average'];

    // Stop solid lines at the previous month
    chartData[mtdIndex]['Portfolio average'] = null;
    chartData[mtdIndex]['Market average'] = null;
  }

  return chartData;
};

export const generateVacancyRatesData = ({
  filteredProperties,
  moodysData,
}: GenerateVacancyRatesDataParams): Array<VacancyRatesChartData> => {
  const chartData: Array<VacancyRatesChartData> = [];

  filteredProperties.forEach((property) => {
    property.breakdownStats.forEach((stat) => {
      const statDate = localDateToDate(stat.statDate);
      const formattedQuarter = format(statDate, 'QQQ yyyy');
      const currentData = chartData.find((data) => data.name === formattedQuarter);
      const quarterNumber = Number(format(statDate, 'Q'));
      const year = statDate.getFullYear();
      const marketAverage = moodysData?.find((data) => data.quarter === quarterNumber && data.year === year);

      if (currentData) {
        currentData['Portfolio average'] =
          (currentData['Portfolio average'] ?? 0) +
          (stat.advertisedUnitsAverage ?? 0) / (property.propertyDetails.numUnits || 1);
        currentData.count++;
      } else {
        chartData.push({
          name: formattedQuarter,
          'Portfolio average': (stat.advertisedUnitsAverage ?? 0) / (property.propertyDetails.numUnits || 1),
          'Market average': marketAverage?.hasVacancyRate ? (marketAverage?.vacancyRate || 0) * 100 : null,
          'Portfolio average quarter to date': null,
          'Market average quarter to date': null,
          'Quarter to date': null,
          timestamp: statDate.getTime(),
          count: 1,
        });
      }
    });
  });

  chartData.forEach((data) => {
    data['Portfolio average'] = ((data['Portfolio average'] ?? 0) / (data.count || 1)) * 100;
  });

  chartData.sort((a, b) => a.timestamp - b.timestamp);

  const currentQuarterFormatted = format(new Date(), 'QQQ yyyy');
  const qtdIndex = chartData.findIndex((data) => data.name === currentQuarterFormatted);

  if (qtdIndex > 0) {
    chartData[qtdIndex - 1]['Portfolio average quarter to date'] = chartData[qtdIndex - 1]['Portfolio average'];
    chartData[qtdIndex - 1]['Market average quarter to date'] = chartData[qtdIndex - 1]['Market average'];
    chartData[qtdIndex]['Portfolio average quarter to date'] = chartData[qtdIndex]['Portfolio average'];
    chartData[qtdIndex]['Market average quarter to date'] = chartData[qtdIndex]['Market average'];

    chartData[qtdIndex]['Portfolio average'] = null;
    chartData[qtdIndex]['Market average'] = null;
  }

  return chartData;
};

export const generateVacantUnitsData = ({
  filteredProperties,
  moodysData,
}: GenerateVacantUnitsDataParams): Array<VacantUnitsChartData> => {
  const chartData: Array<VacantUnitsChartData> = [];

  const monthsByQuarter = new Map<string, Set<string>>();

  filteredProperties.forEach((property) => {
    property.breakdownStats.forEach((stat) => {
      const statDate = localDateToDate(stat.statDate);
      const formattedQuarter = format(statDate, 'QQQ yyyy');
      const formattedMonth = format(statDate, 'yyyy-MM');
      const currentData = chartData.find((data) => data.name === formattedQuarter);
      const quarterNumber = Number(format(statDate, 'Q'));
      const year = statDate.getFullYear();
      const marketAverage = moodysData?.find((data) => data.quarter === quarterNumber && data.year === year);

      if (!monthsByQuarter.has(formattedQuarter)) {
        monthsByQuarter.set(formattedQuarter, new Set());
      }
      monthsByQuarter.get(formattedQuarter)!.add(formattedMonth);

      if (currentData) {
        currentData['Portfolio vacant units'] += Math.max(stat.advertisedUnitsAverage || 0, 0);
        currentData['Portfolio occupied units'] += Math.max(
          property.propertyDetails.numUnits - stat.advertisedUnitsAverage || 0,
          0,
        );
      } else {
        chartData.push({
          name: formattedQuarter,
          'Portfolio vacant units': Math.max(stat.advertisedUnitsAverage || 0, 0),
          'Portfolio occupied units': Math.max(property.propertyDetails.numUnits - stat.advertisedUnitsAverage || 0, 0),
          'Market vacant units': marketAverage?.hasVacancyRate ? marketAverage?.vacantUnits || 0 : 0,
          'Market occupied units': marketAverage?.hasVacancyRate ? marketAverage?.occupiedUnits || 0 : 0,
          'QTD portfolio vacant units': 0,
          'QTD portfolio occupied units': 0,
          'QTD market vacant units': 0,
          'QTD market occupied units': 0,
          timestamp: statDate.getTime(),
        });
      }
    });
  });

  chartData.forEach((data) => {
    const monthCount = monthsByQuarter.get(data.name)?.size || 1;
    data['Portfolio vacant units'] = data['Portfolio vacant units'] / monthCount;
    data['Portfolio occupied units'] = data['Portfolio occupied units'] / monthCount;
  });

  chartData.sort((a, b) => a.timestamp - b.timestamp);

  const currentQuarterFormatted = format(new Date(), 'QQQ yyyy');
  const qtdIndex = chartData.findIndex((data) => data.name === currentQuarterFormatted);

  if (qtdIndex >= 0) {
    chartData[qtdIndex]['QTD portfolio vacant units'] = chartData[qtdIndex]['Portfolio vacant units'];
    chartData[qtdIndex]['QTD portfolio occupied units'] = chartData[qtdIndex]['Portfolio occupied units'];
    chartData[qtdIndex]['QTD market vacant units'] = chartData[qtdIndex]['Market vacant units'];
    chartData[qtdIndex]['QTD market occupied units'] = chartData[qtdIndex]['Market occupied units'];

    chartData[qtdIndex]['Portfolio vacant units'] = 0;
    chartData[qtdIndex]['Portfolio occupied units'] = 0;
    chartData[qtdIndex]['Market vacant units'] = 0;
    chartData[qtdIndex]['Market occupied units'] = 0;
  }

  return chartData;
};
